import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { ArrowLeft, Check, Clapperboard, Gauge, HardDrive, Heart, Layers, ListVideo, Play, RefreshCw, RotateCcw, Server, Star, TriangleAlert, UserRound, Users } from 'lucide-react'
import { applyFocus } from '../hooks/useRemoteNavigation'
import { compactStreamTitle, formatFileSize } from '../lib/format'
import { getCatalogPages, getMeta } from '../lib/stremio'
import { enabledAddons, getStreamsFromAddons } from '../lib/addons'
import { getCompatibilityEvidence } from '../lib/compatibility'
import { getMeasuredSpeed, getWatched } from '../lib/storage'
import { getPersonPortraits } from '../lib/people'
import { groupStreams, type RankedStream } from '../lib/streams'
import type { AdminConfig } from '../lib/config'
import type { HdrCapabilities } from '../lib/hdr'
import type { CodecCapabilities } from '../lib/codecs'
import type { Meta, MetaPreview, Settings, StremioStream, Video, WatchProgress } from '../types'
import { MediaRow } from './MediaRow'
import { TV_ROW_WINDOW, virtualWindow } from '../lib/virtual'

type EpisodeSpacerStyle = CSSProperties & { '--virtual-count': number }

interface Props {
  item: MetaPreview
  settings: Settings
  hdrCapabilities: HdrCapabilities
  codecCapabilities: CodecCapabilities
  progress: WatchProgress[]
  /** open the source list straight away, e.g. after a playback failure */
  autoSources?: { video?: Video } | null
  inLibrary: boolean
  onClose: () => void
  onToggleLibrary: (item: MetaPreview) => void
  onResetProgress: (item: MetaPreview) => void
  onPlay: (stream: StremioStream, meta: Meta, video?: Video) => void
  /** Play: resolve the best source and start it, without showing a list */
  onQuickPlay: (item: MetaPreview, video?: Video) => void
  onSourcesShown: () => void
  onSelect: (item: MetaPreview) => void
  onSearch: (query: string) => void
  onTrailer: (youtubeId: string, title: string) => void
  onLayerChange: (layer: 'details' | 'sources') => void
  config: AdminConfig
}

function episodeLabel(video?: Video) {
  if (!video?.season || !video?.episode) return ''
  return `S${video.season} E${video.episode}`
}

function isResolvedMeta(item: MetaPreview): item is Meta {
  return Object.prototype.hasOwnProperty.call(item, 'videos')
    || Object.prototype.hasOwnProperty.call(item, 'trailers')
    || Object.prototype.hasOwnProperty.call(item, 'cast')
}

function SourceItem({ choice, badge, autofocus, showRisks = true, onSelect }: {
  choice: RankedStream
  badge?: 'best' | 'smaller'
  autofocus?: boolean
  showRisks?: boolean
  onSelect: () => void
}) {
  const note = compactStreamTitle(
    choice.stream.title || choice.stream.description || choice.stream.behaviorHints?.filename || '',
  )
  return (
    <button
      className={`source-item${badge === 'smaller' ? ' source-alternative' : ''}${choice.policyBlocked ? ' source-blocked' : ''}`}
      data-focusable
      data-autofocus={autofocus ? '' : undefined}
      disabled={!choice.allowed}
      aria-disabled={!choice.allowed}
      onClick={onSelect}
    >
      <span className="source-quality">{choice.resolution.label}</span>
      <span className="source-copy">
        <span className="source-heading">
          <strong>{(choice.stream.name || 'Torrentio').replace(/\n/g, ' ')}</strong>
          {badge === 'best' ? <em className="best">Best for this TV</em> : null}
          {badge === 'smaller' ? <em>Smaller file</em> : null}
          {choice.policyBlocked ? <em className="blocked">Blocked by HDR setting</em> : null}
        </span>
        <span className="source-chips">
          <span className={choice.size ? 'good' : ''}><HardDrive size="1.25rem" />{formatFileSize(choice.size)}</span>
          <span><Users size="1.25rem" />{choice.peers ? choice.peers.toLocaleString() : '—'}</span>
          {choice.codec ? <span className={choice.codec === 'h264' ? 'good' : 'risk'}>{choice.codec === 'h264' ? 'H.264' : choice.codec.toUpperCase()}</span> : null}
          {choice.container ? <span className={choice.container === 'avi' ? 'risk' : 'good'}>{choice.container.toUpperCase()}</span> : null}
          {choice.audio ? <span className={choice.audio === 'dts' || choice.audio === 'truehd' ? 'risk' : 'good'}>{choice.audio.toUpperCase()}</span> : null}
          {choice.hdr ? <span className={choice.risks.some((risk) => risk.includes(choice.hdr)) ? 'risk' : 'good'}>{choice.hdr}</span> : null}
          {choice.evidence ? <span className="good"><Check size="1.25rem" />{choice.evidence}</span> : null}
        </span>
        {showRisks && choice.risks.length ? (
          <span className="source-note"><TriangleAlert size="1.25rem" /> {choice.risks.join(' · ')}</span>
        ) : note ? <span className="source-note">{note}</span> : null}
      </span>
      {choice.allowed ? <Play size="1.75rem" fill="currentColor" /> : <TriangleAlert size="1.75rem" />}
    </button>
  )
}

export function DetailOverlay({ item, settings, hdrCapabilities, codecCapabilities, progress, autoSources, inLibrary, onClose, onToggleLibrary, onResetProgress, onPlay, onQuickPlay, onSourcesShown, onSelect, onSearch, onTrailer, onLayerChange, config }: Props) {
  const [meta, setMeta] = useState<Meta>({ ...item })
  const [loading, setLoading] = useState(true)
  const [season, setSeason] = useState<number | null>(null)
  const [sources, setSources] = useState<StremioStream[] | null>(null)
  const [sourceVideo, setSourceVideo] = useState<Video | undefined>()
  const [sourceError, setSourceError] = useState('')
  const [sourceLoading, setSourceLoading] = useState(false)
  const [extraLimit, setExtraLimit] = useState(0)
  const [similar, setSimilar] = useState<MetaPreview[]>([])
  const [portraits, setPortraits] = useState<Record<string, string>>({})
  const sourceToken = useRef(0)

  const panelOpen = sourceLoading || Boolean(sources) || Boolean(sourceError)

  useEffect(() => {
    onLayerChange(panelOpen ? 'sources' : 'details')
    return () => onLayerChange('details')
  }, [onLayerChange, panelOpen])

  useEffect(() => {
    let active = true
    // A related-title or phone selection reuses this overlay. Replace every
    // title-owned field immediately so failed/slow metadata can never leave
    // the previous movie, season or source list on screen.
    sourceToken.current += 1
    setMeta({ ...item })
    setSeason(null)
    setSources(null)
    setSourceVideo(undefined)
    setSourceError('')
    setSourceLoading(false)
    setExtraLimit(0)
    setSimilar([])
    setPortraits({})
    setLoading(true)
    if (isResolvedMeta(item)) {
      setMeta(item)
      const seasons = (item.videos || []).map((video) => video.season).filter(Boolean) as number[]
      if (seasons.length) setSeason(Math.min(...seasons))
      setLoading(false)
      return () => { active = false }
    }
    getMeta(item.type, item.id, item.addonUrl)
      .then((value) => {
        if (!active) return
        setMeta(value)
        const seasons = (value.videos || []).map((video) => video.season).filter(Boolean) as number[]
        if (seasons.length) setSeason(Math.min(...seasons))
      })
      .catch(() => undefined)
      .finally(() => active && setLoading(false))
    return () => { active = false }
  // Keyed on identity fields only. Reopening the same title with a fresh object
  // must not wipe the resolved metadata and refetch it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.addonUrl, item.id, item.type])

  useEffect(() => {
    const genre = (meta.genre || meta.genres || [])[0]
    if (!genre || meta.id !== item.id) { setSimilar([]); return }
    let active = true
    getCatalogPages(meta.type, 'top', { genre }, 1)
      .then((value) => active && setSimilar(value.filter((candidate) => candidate.id !== meta.id).slice(0, 12)))
      .catch(() => active && setSimilar([]))
    return () => { active = false }
  }, [item.id, meta.genre, meta.genres, meta.id, meta.type])

  const castKey = (meta.cast || []).slice(0, 12).join('|')
  useEffect(() => {
    const names = (meta.cast || []).slice(0, 12)
    if (!names.length) { setPortraits({}); return }
    let active = true
    getPersonPortraits(names)
      .then((value) => active && setPortraits(value))
      .catch(() => active && setPortraits({}))
    return () => { active = false }
    // The stable string prevents a new array from restarting the lookup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [castKey])

  const seasons = useMemo(
    () => Array.from(new Set((meta.videos || []).map((video) => video.season).filter(Boolean) as number[])).sort((a, b) => a - b),
    [meta.videos],
  )
  const episodes = useMemo(() => (meta.videos || []).filter((video) => video.season === season), [meta.videos, season])
  // Poster rows mount a nine-card window; this row used to mount a whole season
  // of thumbnails. Same budget, same D-pad hand-off.
  const [episodeStart, setEpisodeStart] = useState(0)
  const episodeList = useRef<HTMLDivElement>(null)
  const pendingEpisode = useRef<number | null>(null)
  const episodeEnd = Math.min(episodes.length, episodeStart + TV_ROW_WINDOW)
  const visibleEpisodes = episodes.slice(episodeStart, episodeEnd)

  useEffect(() => setEpisodeStart(0), [season, meta.id])

  // Metadata can refresh under the row; never leave the window past the end,
  // which would render an empty season.
  useEffect(() => {
    setEpisodeStart((value) => Math.max(0, Math.min(value, Math.max(0, episodes.length - TV_ROW_WINDOW))))
  }, [episodes.length])

  useEffect(() => {
    const row = episodeList.current
    if (!row) return
    const shift = (event: Event) => {
      const detail = (event as CustomEvent<{ index: number; direction: 'left' | 'right' }>).detail
      if (!detail || detail.index < 0 || detail.index >= episodes.length) return
      pendingEpisode.current = detail.index
      setEpisodeStart(virtualWindow(detail.index, episodes.length, TV_ROW_WINDOW, detail.direction).start)
    }
    row.addEventListener('vela:virtual-focus', shift)
    return () => row.removeEventListener('vela:virtual-focus', shift)
  }, [episodes.length])

  useEffect(() => {
    const target = pendingEpisode.current
    if (target === null) return
    const element = episodeList.current?.querySelector<HTMLElement>(`[data-nav-index="${target}"]`)
    if (!element) return
    pendingEpisode.current = null
    applyFocus(element)
  }, [episodeStart])

  const watched = useMemo(() => {
    const map = new Map<string, number>()
    const completed = getWatched()
    progress.forEach((entry) => {
      if (entry.video?.id) map.set(entry.video.id, entry.duration ? entry.currentTime / entry.duration : 0)
    })
    ;(meta.videos || []).forEach((video) => {
      if (completed[`${meta.type}:${meta.id}:${video.id}`]) map.set(video.id, 1)
    })
    return map
  }, [meta.id, meta.type, meta.videos, progress])

  /** Where a viewer should resume: the episode in progress, else the first unwatched. */
  const resumeTarget = useMemo(() => {
    const videos = (meta.videos || []).filter((video) => video.season !== 0)
    if (!videos.length) return undefined
    const found = videos.find((video) => (watched.get(video.id) ?? 0) < 0.92)
    return found || videos[videos.length - 1]
  }, [meta.videos, watched])

  const resumeRatio = resumeTarget ? watched.get(resumeTarget.id) ?? 0 : 0

  const groups = useMemo(
    () => groupStreams(sources || [], {
      runtime: meta.runtime,
      measuredSpeed: getMeasuredSpeed(),
      hdrCapabilities,
      hdrMode: settings.hdrMode,
      codecCapabilities,
      compatibility: getCompatibilityEvidence(),
      codecPolicy: config.playback.codecPolicy,
      maxFileSizeBytes: config.playback.maxFileSizeGb ? config.playback.maxFileSizeGb * 1024 ** 3 : undefined,
      offerAlternative: config.features.smallerAlternative,
    }),
    [codecCapabilities, config.features.smallerAlternative, config.playback.codecPolicy, config.playback.maxFileSizeGb, hdrCapabilities, meta.runtime, settings.hdrMode, sources],
  )
  // groups stay in resolution order because that is what people scan for, so
  // the recommendation badge has to name the genuinely best-scoring stream
  // rather than whichever group happens to be first
  const bestKey = useMemo(() => {
    let key = ''
    let top = -Infinity
    groups.forEach((group) => {
      if (group.primary.allowed && group.primary.score > top) { top = group.primary.score; key = group.resolution.key }
    })
    return key
  }, [groups])

  function closePanel() {
    sourceToken.current += 1
    setSources(null)
    setSourceError('')
    setSourceLoading(false)
    setExtraLimit(0)
    window.setTimeout(() => applyFocus(document.querySelector<HTMLElement>('.detail-actions [data-source-trigger]') || document.querySelector<HTMLElement>('.detail-actions [data-autofocus]')), 0)
  }

  // BACK closes the source panel first, and only then the page
  useEffect(() => {
    const handler = (event: Event) => {
      if (!panelOpen) return
      event.preventDefault()
      closePanel()
    }
    window.addEventListener('vela:back-layer', handler)
    return () => window.removeEventListener('vela:back-layer', handler)
  }, [panelOpen])

  useEffect(() => {
    if (!panelOpen) return
    const timer = window.setTimeout(() => {
      // a comma list resolves in DOM order, which would land on the header's
      // close button — ask for the intended target first
      const panel = document.querySelector('.source-panel')
      applyFocus(
        panel?.querySelector<HTMLElement>('[data-autofocus]')
        || panel?.querySelector<HTMLElement>('[data-focusable]'),
      )
    }, 80)
    return () => clearTimeout(timer)
  }, [panelOpen, sources])

  const findSources = useCallback(async (video?: Video) => {
    const token = sourceToken.current + 1
    sourceToken.current = token
    setSourceVideo(video)
    setSources(null)
    setSourceError('')
    setExtraLimit(0)
    setSourceLoading(true)
    if (!enabledAddons(settings, 'stream').length) {
      setSourceError('Every stream add-on is turned off. Enable one in Settings.')
      setSourceLoading(false)
      return
    }
    try {
      const found = await getStreamsFromAddons(settings, meta.type, video?.id || meta.id)
      if (sourceToken.current !== token) return
      setSources(found)
      if (!found.length) setSourceError('No sources were returned for this title.')
    } catch (error) {
      if (sourceToken.current !== token) return
      setSourceError(error instanceof Error ? error.message : 'Could not load sources.')
    } finally {
      if (sourceToken.current === token) setSourceLoading(false)
    }
  }, [meta.id, meta.type, settings])

  useEffect(() => () => { sourceToken.current += 1 }, [])

  // the player sends people back here when a file will not play, with the
  // list already open so the next step is visible rather than guessed at
  useEffect(() => {
    if (!autoSources) return
    onSourcesShown()
    findSources(autoSources.video)
  }, [autoSources, findSources, onSourcesShown])

  const backdrop = meta.background || item.background
  const otherCount = groups.reduce((total, group) => total + group.others.length, 0)
  const allowedCount = groups.reduce((total, group) => total
    + Number(group.primary.allowed)
    + Number(Boolean(group.alternative?.allowed))
    + group.others.filter((choice) => choice.allowed).length, 0)
  // "Nothing here" is a dead end unless it also says what would change it. Auto
  // blocks an HDR path this TV has not confirmed, and confirmation is exactly
  // what many webOS builds never report — so name the setting that allows it.
  const unconfirmedHdr = groups.some((group) => [group.primary, group.alternative, ...group.others]
    .some((choice) => choice?.policyBlocked && choice.risks.some((risk) => /not confirmed/i.test(risk))))
  const blockedReason = settings.hdrMode === 'sdr'
    ? 'Every source here is HDR, and HDR is switched off in Settings'
    : unconfirmedHdr
      ? 'This TV has not confirmed an HDR path. Settings → HDR playback → Prefer HDR will try it anyway'
      : 'No source matches the current HDR setting'
  const trailer = meta.trailerStreams?.[0]?.ytId || meta.trailers?.[0]?.source
  const directors = Array.isArray(meta.director) ? meta.director : meta.director ? [meta.director] : []
  let extraBudget = extraLimit

  return (
    <div className={`detail-overlay${panelOpen ? ' panel-open' : ''}`} role="dialog" aria-label={meta.name}>
      {/* one way out: the remote's BACK key, and this */}
      <button className="round-button back-button" data-focusable onClick={onClose} aria-label="Back">
        <ArrowLeft size="2rem" />
      </button>

      <div className="detail-scroll">
        {/* Artwork belongs to the opening screenful. Keeping it inside the
            scroll layer makes it leave with the intro instead of sitting
            behind episode copy after the page moves. */}
        <div className="detail-backdrop" style={backdrop ? { backgroundImage: `url(${backdrop})` } : undefined} />
        <div className="detail-shade" />
        <section className="detail-intro">
          {meta.logo ? <img className="detail-logo" src={meta.logo} alt={meta.name} /> : <h1>{meta.name}</h1>}
          <div className="meta-line">
            {meta.imdbRating ? <span className="rating"><Star size="1.5rem" fill="currentColor" /> {meta.imdbRating}</span> : null}
            <span>{meta.releaseInfo || meta.year}</span>
            {meta.runtime ? <span>{meta.runtime}</span> : null}
            {seasons.length ? <span>{seasons.length} {seasons.length === 1 ? 'season' : 'seasons'}</span> : null}
          </div>
          <p>{meta.description}</p>
          <div className="genre-list">
            {(meta.genre || meta.genres || []).slice(0, 4).map((genre) => <span key={genre}>{genre}</span>)}
          </div>
          <div className="detail-actions" data-nav-group="row" data-nav-key="detail-actions">
            {/* Play plays. Picking a specific file is a separate, visible choice. */}
            {meta.type === 'movie' ? (
              <button className="primary-button" data-focusable data-autofocus onClick={() => onQuickPlay(meta)}>
                <Play fill="currentColor" size="1.75rem" /> Play
              </button>
            ) : resumeTarget ? (
              <button className="primary-button" data-focusable data-autofocus onClick={() => onQuickPlay(meta, resumeTarget)}>
                <Play fill="currentColor" size="1.75rem" />
                {resumeRatio > 0.02 ? `Resume ${episodeLabel(resumeTarget)}` : `Play ${episodeLabel(resumeTarget)}`}
              </button>
            ) : null}
            <button className="secondary-button" data-focusable data-source-trigger onClick={() => findSources(meta.type === 'series' ? resumeTarget : undefined)}>
              <ListVideo size="1.75rem" /> Choose source
            </button>
            {trailer && config.features.trailers ? (
              <button className="secondary-button" data-focusable onClick={() => onTrailer(trailer, meta.name)}>
                <Clapperboard size="1.75rem" /> Trailer
              </button>
            ) : null}
            <button className="secondary-button" data-focusable onClick={() => onToggleLibrary(meta)}>
              {inLibrary ? <Check size="1.75rem" /> : <Heart size="1.75rem" />} {inLibrary ? 'In my list' : 'My list'}
            </button>
            {progress.some((entry) => entry.meta.id === meta.id && entry.meta.type === meta.type) ? <button className="secondary-button" data-focusable onClick={() => onResetProgress(meta)}><RotateCcw size="1.75rem" /> Reset progress</button> : null}
          </div>
          {directors.length ? <div className="credit-line"><span>Directed by</span>{directors.map((name) => <button data-focusable onClick={() => onSearch(name)} key={name}>{name}</button>)}</div> : null}
        </section>

        {meta.cast?.length && config.features.cast ? (
          <section className="people-section">
            <h2>Cast</h2>
            <div className="people-row" data-nav-group="row" data-nav-key={`cast:${meta.id}`}>
              {meta.cast.slice(0, 12).map((name) => (
                <button className="person-card" data-focusable onClick={() => onSearch(name)} key={name}>
                  <span>{portraits[name] ? <img src={portraits[name]} alt="" loading="lazy" /> : <UserRound size="2.25rem" />}</span>
                  <strong>{name}</strong>
                  <small>See titles</small>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {meta.type === 'series' && seasons.length ? (
          <section className="episodes-section">
            {/* sticky and opaque, so it never slides under the back button */}
            <div className="episodes-header">
              <h2>Episodes</h2>
              <div className="season-tabs" data-nav-group="row" data-nav-key="seasons">
                {seasons.map((value) => (
                  <button
                    className={`season-tab${season === value ? ' active' : ''}`}
                    data-focusable
                    onClick={() => setSeason(value)}
                    key={value}
                  >Season {value}</button>
                ))}
              </div>
            </div>
            <div ref={episodeList} className="episode-list" data-nav-group="row" data-nav-key={`episodes:${season}`} data-virtual-count={episodes.length}>
              {episodeStart ? <span className="media-row-spacer" aria-hidden="true" style={{ '--virtual-count': episodeStart } as EpisodeSpacerStyle} /> : null}
              {visibleEpisodes.map((video, localIndex) => {
                const index = episodeStart + localIndex
                const ratio = watched.get(video.id) ?? 0
                return (
                  <button className="episode-card" data-focusable data-nav-index={index} onClick={() => onQuickPlay(meta, video)} key={video.id}>
                    <span className="episode-art">
                      {video.thumbnail ? <img src={video.thumbnail} alt="" loading="lazy" /> : null}
                      <i><Play size="2.25rem" fill="currentColor" /></i>
                      {config.features.watchedMarkers && ratio >= 0.92 ? <span className="episode-watched"><Check size="1.25rem" /></span> : null}
                      {ratio > 0.02 && ratio < 0.92 ? (
                        <span className="episode-progress"><i style={{ width: `${ratio * 100}%` }} /></span>
                      ) : null}
                    </span>
                    <span className="episode-copy">
                      <small>Episode {video.episode || index + 1}</small>
                      <strong>{video.name || video.title || `Episode ${video.episode || index + 1}`}</strong>
                      <em>{video.overview || (video.released ? new Date(video.released).toLocaleDateString() : 'Select to play')}</em>
                    </span>
                  </button>
                )
              })}
              {episodeEnd < episodes.length ? <span className="media-row-spacer" aria-hidden="true" style={{ '--virtual-count': episodes.length - episodeEnd } as EpisodeSpacerStyle} /> : null}
            </div>
          </section>
        ) : null}
        {similar.length && config.features.similar ? (
          <section className="similar-section">
            <MediaRow title="More like this" items={similar} onSelect={onSelect} />
          </section>
        ) : null}
        {loading ? <div className="detail-loading">Loading title details…</div> : null}
      </div>

      {panelOpen ? (
        <div className="source-panel">
          <div className="source-panel-header">
            <div>
              <Server size="2rem" />
              <span>
                <small>SOURCES</small>
                <strong>{sourceVideo ? `${episodeLabel(sourceVideo)} · ${sourceVideo.name || sourceVideo.title || meta.name}` : meta.name}</strong>
              </span>
            </div>
            <button className="round-button" data-focusable onClick={closePanel} aria-label="Close sources">
              <ArrowLeft size="1.75rem" />
            </button>
          </div>
          {sourceLoading ? (
            <div className="source-loading"><i /><strong>Finding sources</strong><span>This can take a few seconds</span></div>
          ) : null}
          {sourceError ? (
            <div className="source-error">
              <strong>Sources unavailable</strong>
              <span>{sourceError}</span>
              <div className="retry-actions">
                <button className="secondary-button" data-focusable data-autofocus onClick={() => findSources(sourceVideo)}>
                  <RefreshCw size="1.5rem" /> Try again
                </button>
              </div>
            </div>
          ) : null}
          {groups.length ? (
            <div className="source-list" data-nav-group="col" data-nav-key="sources">
              <div className={`source-summary${allowedCount ? '' : ' blocked'}`}>
                {allowedCount ? <Gauge size="1.25rem" /> : <TriangleAlert size="1.25rem" />}
                {allowedCount ? 'Ranked for this TV and connection' : blockedReason}
              </div>
              {groups.map((group) => {
                const extras = group.others.slice(0, extraBudget)
                extraBudget -= extras.length
                return <div className="source-group" key={group.resolution.key}>
                  <SourceItem
showRisks={config.features.sourceRisks}                     choice={group.primary}
                    badge={group.resolution.key === bestKey ? 'best' : undefined}
                    autofocus={group.resolution.key === bestKey}
                    onSelect={() => onPlay(group.primary.stream, meta, sourceVideo)}
                  />
                  {group.alternative ? (
                    <SourceItem showRisks={config.features.sourceRisks} choice={group.alternative} badge="smaller" onSelect={() => onPlay(group.alternative!.stream, meta, sourceVideo)} />
                  ) : null}
                  {extras.map((choice, position) => (
                    <SourceItem showRisks={config.features.sourceRisks} key={`${choice.stream.infoHash || choice.stream.url}:${position}`} choice={choice} onSelect={() => onPlay(choice.stream, meta, sourceVideo)} />
                  ))}
                </div>
              })}
              {otherCount && config.features.showAllSources ? (
                <button className="source-more" data-focusable onClick={() => setExtraLimit((value) => value >= otherCount ? 0 : Math.min(otherCount, value + 24))}>
                  <Layers size="1.5rem" /> {extraLimit >= otherCount ? 'Hide extra sources' : `Show ${Math.min(24, otherCount - extraLimit)} more · ${otherCount - extraLimit} remaining`}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
